// Kueri PostgREST ber-filter `in.(…)` untuk daftar id yang bisa panjang.
//
// Helper murni (tanpa Supabase) supaya bisa diuji — lihat
// src/lib/__tests__/inChunks.test.ts.
//
// KENAPA ADA (bughunt ketiga, 2026-09-30): Keranjang/Pesanan pembeli dan
// Keranjang Pelanggan admin mengirim SELURUH id item sepanjang masa dalam SATU
// GET `invoice_lines?order_item_id=in.(…)`. Pembeli zzhomey ber-683 item
// membuat query string ±26,5 KB → gateway menolak 400 SEBELUM sampai ke
// database (log edge PROD: GET ber-JWT lolos sampai ±23,5 KB, gagal di
// ±26,5 KB), 4 dari 4 kali — dan galatnya ditelan (`data ?? []`), jadi
// Keranjang tampil seolah tak ada item yang sudah masuk nota.
//
// Aturannya:
//   • id dipecah per `IN_FILTER_CHUNK` (±6 KB URL per potongan) dan
//     potongannya dijalankan PARALEL — satu round-trip, bukan N berurutan;
//   • id kembar dibuang lebih dulu (potongan tak saling tumpang);
//   • GAGAL SATU = GAGAL SEMUA: galat potongan mana pun DILEMPAR. Hasil
//     sebagian yang dirakit diam-diam adalah bentuk lain dari "data ?? []".

/**
 * Id per filter `in.(…)`. Uuid ±36 karakter + pemisah ter-encode (`%2C`) ≈ 39
 * karakter → 150 id ≈ 6 KB, jauh di bawah batas gateway, dan baris per
 * potongan tetap jauh di bawah plafon 1.000 baris PostgREST.
 */
export const IN_FILTER_CHUNK = 150;

/** Pecah daftar (tanpa kembar, urutan pertama dipertahankan) jadi potongan ≤ `size`. */
export function chunkList<T>(items: readonly T[], size: number = IN_FILTER_CHUNK): T[][] {
  if (!Number.isInteger(size) || size <= 0) {
    throw new Error(`Ukuran potongan tidak sah: ${size}`);
  }
  const unique = Array.from(new Set(items));
  const out: T[][] = [];
  for (let i = 0; i < unique.length; i += size) {
    out.push(unique.slice(i, i + size));
  }
  return out;
}

/** Bentuk minimal balikan query builder supabase-js (thenable). */
export type ChunkQueryResult = {
  data: unknown;
  error: {
    message: string;
    code?: string;
    details?: string | null;
    hint?: string | null;
  } | null;
};

/**
 * Jalankan `run` untuk tiap potongan id secara paralel dan gabungkan barisnya.
 * Daftar kosong → [] tanpa satu pun query. Galat potongan mana pun → dilempar
 * sebagai `Error` dengan pesan galat aslinya DAN `code`/`details`/`hint`-nya:
 * `terjemahkanErrorRpc` memetakan galat lewat `code` (42501, 23514, …), dan
 * galat yang kehilangan kodenya jatuh ke pesan mentah PostgREST.
 */
export async function selectInChunks(
  ids: readonly string[],
  run: (chunk: string[]) => PromiseLike<ChunkQueryResult>,
  size: number = IN_FILTER_CHUNK
): Promise<unknown[]> {
  const chunks = chunkList(ids, size);
  if (chunks.length === 0) return [];
  const results = await Promise.all(chunks.map((chunk) => run(chunk)));
  const rows: unknown[] = [];
  for (const res of results) {
    if (res.error) {
      const { message, code, details, hint } = res.error;
      throw Object.assign(new Error(message), { code, details, hint });
    }
    if (Array.isArray(res.data)) rows.push(...res.data);
  }
  return rows;
}
